/**
 * The pill that says which side of the business you are on, and the door between the two.
 *
 * ## How the module is shown, and why it is not a badge
 *
 * It was a pill in the header. The operator's verdict on it was short — *"I don't like that pill
 * either for the development one"* — and the instinct behind it is right: a small badge is a label
 * you have to go and read, competing with the two shortcuts beside it, and it was carrying a whole
 * change of business on a 12px word.
 *
 * **The rail itself is the indicator now.** It changes colour with the module, so the answer to
 * "which side am I on" is the largest object on the screen rather than the smallest — seen without
 * being read, and impossible to miss when the two rails share the words Clients, Payments and
 * Reports over two different books. See `MODULE_SKIN` in `components/layout.tsx`.
 *
 * ## Why switching asks first
 *
 * Changing module is not navigation. It changes what every screen means: the rail, the shortcuts in
 * the header, the vocabulary, and which of two books the numbers are drawn from. Somebody who
 * pressed it by accident — and the two tabs sit adjacent, one press apart — arrives on a dashboard
 * whose figures look wrong rather than different, and the honest reaction to that is to think the
 * product has broken.
 *
 * The dialog is therefore not a "are you sure" over a harmless act. It is the one place the product
 * explains that these are two businesses, says what is about to change, and names what is *not*
 * changing — because the fear it has to answer is "have I just moved my data".
 */

import type { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { MODULE_INFO, moduleHome, type ModuleCode } from "@rentorng/core";
import { Button, Modal } from "./ui";

/* ------------------------------------------------------------------------ the marks

   Flat, two-tone, one idea each: a building let out, and a plot being sold. `aria-hidden`, because
   each sits beside text that already says the same thing.

   **These are the one place in the application that must NOT use the brand tokens.** Everything
   else reads `--color-brand-*` so that it follows the module skin (ADR-0158) — and that is exactly
   wrong here. The letting mark was drawn in `fill-brand-*`, so on the development skin, where those
   tokens *are* blue, it turned blue too: two blue marks either side of an arrow, in a dialog whose
   whole job is to show that these are two different things.

   A mark that identifies a module has to mean the same thing in both modules. So the colours are
   literal: letting is the brand green and development is the module's blue, always, whichever skin
   is in force. `tools/verify-rail.mjs` asserts they stay different from each other.
*/

/** Letting's green and development's blue, pinned. See the note above. */
const LETTING_INK = { wash: "#ecfdf5", solid: "#12805c", soft: "#d1fae5", deep: "#0c5340" };
const DEVELOPING_INK = { wash: "#eef4fd", solid: "#1450a8", deep: "#003896" };

function LettingMark({ size = 96 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" fill="none" aria-hidden="true">
      <rect x="6" y="6" width="84" height="84" rx="20" fill={LETTING_INK.wash} />
      {/* A block of flats: the thing that is let, drawn as the roof and the windows that are lit. */}
      <path d="M24 46 48 26l24 20v28a4 4 0 0 1-4 4H28a4 4 0 0 1-4-4V46Z" fill={LETTING_INK.solid} />
      <rect x="34" y="52" width="10" height="10" rx="2" fill={LETTING_INK.wash} />
      <rect x="52" y="52" width="10" height="10" rx="2" fill={LETTING_INK.wash} />
      <rect x="43" y="66" width="10" height="12" rx="2" fill={LETTING_INK.soft} />
      {/* The key: a tenancy is possession handed over, not ownership. */}
      <circle cx="70" cy="30" r="9" fill={LETTING_INK.deep} />
      <circle cx="70" cy="30" r="3.5" fill={LETTING_INK.wash} />
      <path d="M70 39v11m0-5h5" stroke={LETTING_INK.deep} strokeWidth="3.5" strokeLinecap="round" />
    </svg>
  );
}

function DevelopmentMark({ size = 96 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" fill="none" aria-hidden="true">
      <rect x="6" y="6" width="84" height="84" rx="20" fill={DEVELOPING_INK.wash} />
      {/* Plots on a plan: what a developer sells is a parcel before it is a building. */}
      <rect x="22" y="42" width="24" height="20" rx="3" fill={DEVELOPING_INK.solid} />
      <rect x="50" y="42" width="24" height="20" rx="3" fill={DEVELOPING_INK.solid} opacity="0.55" />
      <rect x="22" y="66" width="24" height="12" rx="3" fill={DEVELOPING_INK.solid} opacity="0.35" />
      <rect x="50" y="66" width="24" height="12" rx="3" fill={DEVELOPING_INK.solid} opacity="0.35" />
      {/* Sold: the one that has a buyer against it. */}
      <circle cx="66" cy="30" r="11" fill={DEVELOPING_INK.deep} />
      <path d="M61 30.5l3.5 3.5 6.5-7" stroke={DEVELOPING_INK.wash} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

const MARKS: Record<ModuleCode, (props: { size?: number }) => ReactElement> = {
  letting: LettingMark,
  developing: DevelopmentMark,
};

/* ------------------------------------------------------------------------ the door */

/**
 * Asked before the module changes.
 *
 * Both marks are shown, with an arrow between them, because the question is not "do you want to go
 * here" — it is "do you understand that you are leaving the other one". Showing only the
 * destination would make it read as ordinary navigation, which is the misunderstanding this exists
 * to prevent.
 */
export function ModuleSwitchModal({
  from,
  to,
  onClose,
}: {
  from: ModuleCode;
  to: ModuleCode | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  if (!to) return null;

  const leaving = MODULE_INFO[from];
  const going = MODULE_INFO[to];
  const FromMark = MARKS[from];
  const ToMark = MARKS[to];

  return (
    <Modal open onClose={onClose} title={`Switch to ${going.label}`}>
      <div data-module-switch-modal>
        <div className="flex items-center justify-center gap-3 py-2">
          <span className="opacity-40">
            <FromMark size={72} />
          </span>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0 text-ink-400">
            <path d="M4 12h15m0 0-5.5-5.5M19 12l-5.5 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <ToMark size={72} />
        </div>

        <p className="mt-3 text-sm text-ink-700">
          <strong className="font-semibold text-ink-900">{going.label}</strong> is a different side of
          the business. {going.blurb}
        </p>

        <ul className="mt-3 space-y-1.5 text-sm text-ink-600">
          <li className="flex gap-2">
            <span aria-hidden className="mt-1.5 size-1.5 shrink-0 rounded-full bg-ink-300" />
            <span>
              The menu, the shortcuts and the wording all change to {going.label.toLowerCase()}.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden className="mt-1.5 size-1.5 shrink-0 rounded-full bg-ink-300" />
            <span>
              Figures are drawn from the {going.noun}'s book, so the totals will differ from the ones
              on screen now.
            </span>
          </li>
          {/* The fear this dialog actually has to answer. Said plainly and last, where it is read. */}
          <li className="flex gap-2">
            <span aria-hidden className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand-500" />
            <span>
              <strong className="font-medium text-ink-800">Nothing moves and nothing is lost.</strong>{" "}
              Your {leaving.label.toLowerCase()} records stay exactly as they are, and you can come
              back whenever you like.
            </span>
          </li>
        </ul>

        {/* Stacked on a phone, side by side once there is room.
            The two labels carry module names, so together they are 363px wide against the 348px a
            390px handset gives — they wrapped, and against a right-aligned row that left one button
            alone on the first line and the other beneath it, staggered. Full width below `sm` is the
            honest answer: the labels are long because they are specific, and shortening them to
            "Stay" and "Switch" would lose the thing that makes the choice clear.

            The primary sits **last**, which is nearest the thumb on a sheet that rises from the
            bottom edge. */}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
          <Button variant="secondary" className="justify-center" onClick={onClose}>
            Stay in {leaving.label}
          </Button>
          <Button
            className="justify-center"
            data-module-switch-confirm
            onClick={() => {
              onClose();
              /* `moduleHome`, never `going.home`. Letting's home is `/app`, which **no module
                 owns** — so navigating there leaves `useActiveModule` falling back to what it
                 last remembered, which is the module being left. The switch then appears to work
                 (the address changes, the rail redraws) while the rail's own tab stays selected on
                 the old one. `moduleHome` appends `?module=` exactly when the path cannot speak for
                 itself. The rail's link already did this and said why; this dialog did not, and
                 reintroduced the bug it warns about. */
              navigate(moduleHome(to));
            }}
          >
            Switch to {going.label}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
